<template>
  <div>
    <v-card dense>
      <v-card-title>
        <span class="headline">Screening Timeline</span>
      </v-card-title>
      <v-divider class="mt-0"></v-divider>
      <v-card-text v-if="events.length > 0">
        <v-row align="center" no-gutters>
          <v-col cols="2">
            <v-select
              label="Filter by group"
              :items="
                spaceObjectGroups.map(
                  (spaceObjectGroup) => spaceObjectGroup.name
                )
              "
              v-model="groupFilter"
            ></v-select>
          </v-col>
          <v-col cols="10">
            <div id="screening-timeline-plot"></div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text v-else>
        <v-alert type="info" border="left" prominent>
          No events detected
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Plotly from 'plotly.js-dist'
import * as d3 from 'd3'
import { mapGetters, mapActions } from 'vuex'
export default {
  name: 'ScreeningCatalogueAnalysisDashboardTimeline',
  props: {
    events: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      data: [],
      groupFilter: 'ALL'
    }
  },

  mounted() {
    if (this.events.length === 0) {
      return
    }
    this.createTimeline()

    window.addEventListener('resize', this.createTimeline)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.createTimeline)
  },

  watch: {
    currentEvents() {
      if (this.events.length > 0) {
        this.createTimeline()
      }
    }
  },

  computed: {
    ...mapGetters({
      currentResultsLog: 'currentResultsLog'
    }),
    spaceObjectGroups() {
      let spaceObjectGroups = [{ name: 'ALL' }]

      let alertDefinitions =
        this.currentResultsLog.results.analysisResult.alertDefinitions
      for (const alert of alertDefinitions) {
        const currentNames = spaceObjectGroups.map((group) => group.name)
        if (alert.primarySpaceObjectGroups !== null) {
          for (const currentGroup of alert.primarySpaceObjectGroups) {
            if (!currentNames.includes(currentGroup.name)) {
              spaceObjectGroups.push(currentGroup)
            }
          }
        }
      }
      return spaceObjectGroups
    },
    currentGroup() {
      return this.spaceObjectGroups.find(
        (spaceObjectGroup) => spaceObjectGroup.name === this.groupFilter
      )
    },
    currentEvents() {
      if (this.currentGroup.name === 'ALL') {
        return this.events
      } else {
        let satellitesInGroup = this.currentGroup.spaceObjectIdentifiers

        return this.events.filter((event) =>
          satellitesInGroup.includes(event.eventTargetNoradId)
        )
      }
    }
  },

  methods: {
    ...mapActions(['setDisplayedScreeningEvent', 'setDisplayedScreeningTab']),
    computeWidthForInterval(scaleX, event) {
      /* in case we want to use the duration of the event
      let keyTime = 'detectionDate'
      if (event.eventType === 'CLOSE_APPROACH') {
        keyTime = 'closeApproachDate'
      }
      const start = new Date(event[keyTime])
      const end = new Date(start.getTime() + secondsFromStart * 1000)
      return scaleX(end) - scaleX(start)
      */
      return 3
    },

    computePosition(scaleX, event) {
      const start = this.retrieveEventDate(event)
      return scaleX(start)
    },

    computeVisibility(scaleX, event) {
      return this.computePosition(scaleX, event) < 0 ? 'hidden' : 'visible'
    },

    retrieveEventDate(event) {
      let keyTime = 'detectionDate'
      if (event.eventType === 'CLOSE_APPROACH') {
        keyTime = 'closeApproachDate'
      }
      return new Date(event[keyTime])
    },

    createTimeline() {
      const existingSpacecrafts = new Map()
      const events = this.currentEvents.map((event) => {
        if (existingSpacecrafts.has(event.eventTargetNoradId)) {
          existingSpacecrafts.set(
            event.eventTargetNoradId,
            existingSpacecrafts.get(event.eventTargetNoradId)
          )
          return {
            ...event,
            id: existingSpacecrafts.get(event.eventTargetNoradId)
          }
        } else {
          const id = existingSpacecrafts.size
          existingSpacecrafts.set(event.eventTargetNoradId, id)
          return {
            ...event,
            id: id
          }
        }
      })

      const uniqueSpacecrafts = new Set(
        events.map((event) => event.eventTargetNoradId)
      )

      const margin = { top: 0, right: 20, bottom: 20, left: 50 }

      const div = document.getElementById('screening-timeline-plot')
      div.innerHTML = '' // Clear the div, so it can be redrawn on resize / filter change
      const width = div.clientWidth - margin.left - margin.right
      const heightPerSpacecraft = 50

      const height = uniqueSpacecrafts.size * heightPerSpacecraft

      const x_domain = d3.extent(
        events.map((event) => {
          return this.retrieveEventDate(event)
        })
      )

      let leftDate = new Date(x_domain[0])
      leftDate = new Date(leftDate.setDate(leftDate.getDate() - 1))

      let rightDate = new Date(x_domain[1])
      rightDate = new Date(rightDate.setDate(rightDate.getDate() + 1))

      // Create a new scale for the timeline
      const x = d3
        .scaleUtc()
        .domain([leftDate, rightDate])
        .range([0, width])
        .nice()

      const y = d3
        .scaleBand()
        .domain(Array.from(uniqueSpacecrafts))
        .range([0, height])

      const yAxis = d3.axisLeft().scale(y)

      // Create a new axis for the timeline
      const xAxis = d3.axisBottom().scale(x)

      const zoom = d3
        .zoom()
        .scaleExtent([1, 100000])
        .translateExtent([
          [0, 0],
          [width, height]
        ])
        .on('zoom', () => onZoom.bind(this)()) // Bind this to the onZoom function

      // Create a new svg element to contain the timeline
      const svg = d3
        .select('#screening-timeline-plot')
        .append('svg')
        .call(zoom)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')

      // Add the axis to the svg element
      const g = svg
        .append('g')
        .attr('transform', 'translate(0,' + height + ')')
        .call(xAxis)

      svg.append('g').call(yAxis)

      // Add the events to the timeline
      svg
        .selectAll('rect')
        .data(events)
        .enter()
        .append('rect')
        .attr('x', (d) => this.computePosition(x, d))
        .attr('y', (d, i) => {
          return d.id * heightPerSpacecraft
        })
        .attr('width', (d, i) => {
          return this.computeWidthForInterval(x, d)
        })
        .attr('height', heightPerSpacecraft)
        .attr('fill', (d) => d.criticity.color)
        .attr('stroke', (d) => d.criticity.color)
        .attr('stroke-width', 3)
        .attr('opacity', 0.5)
        .style('visibility', (d) => this.computeVisibility(x, d))
        .style('cursor', 'pointer')
        .on('click', (d) => {
          this.setDisplayedScreeningEvent(d)
          this.setDisplayedScreeningTab(1)
        })
        .append('title')
        .text((d) => {
          if (d.eventType === 'CLOSE_APPROACH') {
            return `
${d.criticity.name + ' : ' + d.eventType}\n
Close approach date: ${d.closeApproachDate}
`
          } else {
            return `
${d.criticity.name + ' : ' + d.eventType}\n
Start: ${d.detectionDate}
`
          }
        })

      // declare zoom function at the end so we can use the closure of all the variables
      function onZoom() {
        const t = d3.event.transform
        const xt = t.rescaleX(x)

        g.call(xAxis.scale(xt))

        svg
          .selectAll('rect')
          .style('visibility', (d) => this.computeVisibility(xt, d))
          .attr('x', (d) => this.computePosition(xt, d))
          .attr('width', (d, i) => {
            return this.computeWidthForInterval(xt, d)
          })
      }
    }
  }
}
</script>
